import React from 'react';
import {Autocomplete, createTheme, InputAdornment, TextField, ThemeProvider} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import {bgColorRequester, bgColorSpender} from '../../_helpers';

/**
 * @param {ControlledEZPRSelect.propTypes} props
 * @return {JSX.Element}
 */
export function ControlledEZPRSelect(props) {
    const style = {
        width: '100%',
        height: '100%',
        minWidth: props.minWidth,
    };

    const stableId = React.useRef(Date.now() * Math.random()).current;

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                    '& .css-19qh8xo-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                        '-webkit-text-fill-color': props.darkerTextWhenDisabled ?'rgba(0,0,0,0.87)' : '',
                    },
                },
                white: {
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: props.error ? 'red' : 'white',
                    },
                    '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'lightgrey',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: props.error ? 'red' : 'white',
                    },
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(255,255,255,1.0)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(255,255,255,1.0)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                        color: props.error ? 'red' : 'white',
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                        color: props.error ? 'red' : 'white',
                    },
                    '& .MuiAutocomplete-input': {
                        color: 'white',
                    },
                },
            };
        },
        {_theme},
    );

    const classes = useStyles();

    const dispatchChange = (synthEvent, newValue) => {
        if (typeof props.handleChange === 'function') {
            if ( props.valueFromSelectionOnly && typeof newValue === 'string') {
                return;
            }
            props.handleChange(synthEvent, newValue);
        }
    };

    const handleAutoCompleteChange = (synthEvent, newValue) => {
        dispatchChange(synthEvent, newValue);
    };

    const handleTextAreaChange = (synthEvent) => {
        const newValue = synthEvent.target.value;
        dispatchChange(synthEvent, newValue);
    };

    const handleOnKeyDown = (e) => {
        if (props.preventTyping) {
            e.preventDefault();
        }
    };
    // Element
    return (
        <ThemeProvider theme={EZPRTheme}>
            <div className={props.isWhite ? classes.white : classes.root}>
                <Autocomplete
                    key={props.name + stableId}
                    {...(props.openAutocomplete && {open: true})}
                    id={props.name + stableId}
                    sx={{
                        backgroundColor: props.mandatoryForSpender && props.highlightMandatoryFieldForSpender ?
                            bgColorSpender :
                            props.mandatoryForRequester && props.highlightMandatoryFieldForRequester ?
                                bgColorRequester :
                                null,
                    }}
                    clearOnBlur={props.valueFromSelectionOnly}
                    forcePopupIcon={!props.readOnly}
                    freeSolo={props.freeSolo !== undefined ? props.freeSolo : true}
                    options={props.options}
                    style={style}
                    size={'small'}
                    value={props.value}
                    loading={props.loading}
                    onChange={handleAutoCompleteChange}
                    groupBy={props.groupBy}
                    disabled={props.readOnly}
                    renderInput={(params) => <TextField
                        helperText={props.helperText}
                        inputRef={props.inputRef}
                        name={props.name} error={props.error}
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: ( props.loading &&
                                    <InputAdornment position="start">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                            ),
                        }}
                        variant={'outlined'}
                        placeholder={props.placeholder}
                        onChange={handleTextAreaChange}
                        onKeyDown={handleOnKeyDown}/>
                    }
                />
            </div>
        </ThemeProvider>
    );
}

ControlledEZPRSelect.propTypes = {
    options: PropTypes.array,
    isWhite: PropTypes.bool,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    inputRef: PropTypes.object,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    handleChange: PropTypes.func,
    name: PropTypes.string,
    readOnly: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchFunction: PropTypes.func,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    minWidth: PropTypes.string,
    freeSolo: PropTypes.bool,
    helperText: PropTypes.string,
    darkerTextWhenDisabled: PropTypes.bool,
    groupBy: PropTypes.func,
    mandatoryForSpender: PropTypes.bool,
    mandatoryForRequester: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    preventTyping: PropTypes.bool,
    valueFromSelectionOnly: PropTypes.bool,
    openAutocomplete: PropTypes.bool,
};

ControlledEZPRSelect.defaultProps = {
    readOnly: false,
    preventTyping: false,
    valueFromSelectionOnly: false,
    openAutocomplete: false,
    name: '',
};
